<template>
  <section class="container-full-5 collectivites-onboarding">
    <h2 class="secondary-font gap-4 mt-2 mb-6">Onboarding - Création de compte</h2>

    <div v-if="pageLoading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-else>
      <div v-if="errorMessage" class="alert alert-danger mb-4">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-4">
        {{ successMessage }}
      </div>

      <!-- Stepper/Wizard Navigation -->
      <div class="wizard-stepper mb-6">
        <div class="stepper-container">
          <div 
            class="stepper-step" 
            :class="{ active: currentStep === 1, completed: currentStep > 1 }"
            @click="goToStep(1)"
          >
            <div class="stepper-circle">
              <span v-if="currentStep > 1">✓</span>
              <span v-else>1</span>
            </div>
            <div class="stepper-label">Informations</div>
          </div>
          <div class="stepper-line" :class="{ completed: currentStep > 1 }"></div>
          <div 
            class="stepper-step" 
            :class="{ active: currentStep === 2, completed: currentStep > 2 }"
            @click="currentStep > 1 ? goToStep(2) : null"
          >
            <div class="stepper-circle">
              <span v-if="currentStep > 2">✓</span>
              <span v-else>2</span>
            </div>
            <div class="stepper-label">Pièces d'identité</div>
          </div>
          <div class="stepper-line" :class="{ completed: currentStep > 2 }"></div>
          <div 
            class="stepper-step" 
            :class="{ active: currentStep === 3, completed: currentStep > 3 }"
            @click="currentStep > 2 ? goToStep(3) : null"
          >
            <div class="stepper-circle">
              <span v-if="currentStep > 3">✓</span>
              <span v-else>3</span>
            </div>
            <div class="stepper-label">Bulletins</div>
          </div>
        </div>
      </div>

      <!-- Step 1: Informations personnelles + Engagement -->
      <div v-show="currentStep === 1" class="widget px-8 py-7 s20">
        <OnboardingPersonalInfosForm
          :collectivites="formParams.collectivites"
          :default-collectivite-id="defaultCollectiviteId"
          @saved="onAccountCreated"
        />
      </div>

      <!-- Step 2: Pièces d'identité -->
      <div v-show="currentStep === 2" class="widget px-8 py-7 s20">
        <div v-if="formDatas.user">
          
          <OnboardingIdentityDocumentsForm
            :user="{
              nom: formDatas.user.nom || '',
              prenom: formDatas.user.prenom || '',
              email: formDatas.user.email || '',
              telephone: formDatas.user.telephone || ''
            }"
            :types_piece_identites="formParams.types_piece_identites"
            @saved="onIdentityDocumentsSaved"
          />
          <div class="flex justify-content-between mt-6">
            <button class="btn white2" @click="goToStep(1)">
              <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
              <span>Précédent</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Step 3: Bulletins de salaire -->
      <div v-show="currentStep === 3" class="widget px-8 py-7 s20">
        <div v-if="formDatas.user">
          <div class="tag-state lg rounded-50 flex align-items-center gap-4 px-4 py-4 mb-6">
            <img
              v-if="formDatas.photo?.preview && typeof formDatas.photo.preview === 'string'"
              :src="formDatas.photo.preview"
              class="rounded-100 overflow-hidden size-2xl"
              alt="Photo de profil"
            />
            <div v-else class="rounded-100 overflow-hidden size-2xl bg-grey-200 flex-center">
              <img src="/public/icons/icon-profil.svg" width="60%" class="opacity-50" alt="" />
            </div>
            <div class="px-3">
              <h5 class="my-1">{{ formDatas.user.prenom }} {{ formDatas.user.nom }}</h5>
              <span class="text-sm">{{ formDatas.user.email }} | {{ getCollectiviteName(formDatas.user.collectivite_id) }}</span>
            </div>
            <div class="rounded-100 overflow-hidden size-2xl ml-auto">
              <img src="/public/icons/icon-coop-finance.svg" width="100%" alt="Coop Finance" />
            </div>
          </div>

          <h2 class="font-semibold text-center my-6">Charger vos 3 derniers bulletins de salaire</h2>

          <div class="form-field-floating reactive my-5">
            <label class="form-switch lg">
              <div class="switch">
                <input type="checkbox" name="send_kyc_email" id="send_kyc_email" v-model="sendKycEmail" />
                <span class="slider round"></span>
              </div>
              <h6 class="label text-grey-600">Envoyer l'email de confirmation KYC</h6>
            </label>
          </div>

          <OnboardingPayslipDocumentsForm
            :form-params="{
              user: {
                nom: formDatas.user.nom || '',
                prenom: formDatas.user.prenom || '',
                email: formDatas.user.email || '',
                telephone: formDatas.user.telephone || ''
              },
              photo: formDatas.photo
            }"
            :loading="submitLoading"
            @saved="onPayslipDocumentsSaved"
          />
          <div class="flex justify-content-between mt-6">
            <button class="btn white2" @click="goToStep(2)" :disabled="submitLoading">
              <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
              <span>Précédent</span>
            </button>
            <button 
              class="btn" 
              @click="doSaveKYCDocuments" 
              :disabled="submitLoading || !formDatas.bulletin1 || !formDatas.bulletin2 || !formDatas.bulletin3"
            >
              <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
              <span v-if="!submitLoading">Soumettre l'onboarding</span>
              <span v-else>Soumission en cours...</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="clear-both h-70"></div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import OnboardingPersonalInfosForm from '@/components/collectivites/onboarding/OnboardingPersonalInfosForm.vue'
import OnboardingIdentityDocumentsForm from '@/components/collectivites/onboarding/OnboardingIdentityDocumentsForm.vue'
import OnboardingPayslipDocumentsForm from '@/components/collectivites/onboarding/OnboardingPayslipDocumentsForm.vue'
import onboardingService from '@/services/onboardingService'
import membersService from '@/services/membersService'
import { showAlert } from '@/utils/swal'
import authService from '@/services/authService'
import { TokenStorage } from '@/services/apiConfig'

const currentStep = ref(1)

interface Collectivite {
  id: number
  nom: string
}

interface PersonalInfosPayload {
  nom: string | null
  prenom: string | null
  date_naissance: string | null
  age: number
  date_engagement: string | null
  anciennete: number
  collectivite_id: number | null
  salaire: string
  quotite_cessible_pourcent: string
  quotite_cessible_montant: string
  telephone: string | null
  email: string | null
}

interface UploadedFile {
  file: File
  name: string
  preview?: string | ArrayBuffer | null
}

const pageLoading = ref(true)
const submitLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const defaultCollectiviteId = ref<number | null>(null)
const sendKycEmail = ref(true)

const formParams = reactive({
  collectivites: [] as Collectivite[],
  types_piece_identites: {} as Record<string, string>
})

const formDatas = reactive<{
  user: PersonalInfosPayload | null
  photo: UploadedFile | null
  type_piece_identite: string | null
  numero_piece_identite: string | null
  piece_recto: UploadedFile | null
  piece_verso: UploadedFile | null
  bulletin1: UploadedFile | null
  bulletin2: UploadedFile | null
  bulletin3: UploadedFile | null
}>({
  user: null,
  photo: null,
  type_piece_identite: null,
  numero_piece_identite: null,
  piece_recto: null,
  piece_verso: null,
  bulletin1: null,
  bulletin2: null,
  bulletin3: null
})

onMounted(async () => {
  await loadCollectivite()
  await loadFormDatas()
})

async function loadCollectivite() {
  try {
    // Récupérer la collectivité du membre connecté
    let currentUser = authService.getCurrentUser()
    
    // Si les données utilisateur ne contiennent pas la collectivité, récupérer le profil complet
    if (currentUser && !currentUser.collectivite_id && !currentUser.collectivite?.id && !(currentUser as any).collectiviteId) {
      const profileResponse = await authService.getProfile()
      if (profileResponse.status === 0 && profileResponse.data) {
        currentUser = profileResponse.data
        // Mettre à jour les données utilisateur dans le stockage
        TokenStorage.setUser(currentUser)
      }
    }
    
    if (currentUser) {
      // L'API peut retourner collectiviteId (camelCase) ou collectivite_id (snake_case)
      const collectiviteId = currentUser.collectivite_id || (currentUser as any).collectiviteId || currentUser.collectivite?.id
      
      if (collectiviteId) {
        defaultCollectiviteId.value = collectiviteId
      }
    }
  } catch (error: any) {
    console.error('Erreur lors du chargement de la collectivité:', error)
  }
}

async function loadFormDatas() {
  pageLoading.value = true
  errorMessage.value = ''
  try {
    const response = await onboardingService.getFormDatas()
    formParams.collectivites = response.collectivites || []
    formParams.types_piece_identites = response.types_piece_identites || {}
  } catch (error: any) {
    errorMessage.value = error.message || 'Impossible de charger les données du formulaire.'
  } finally {
    pageLoading.value = false
  }
}

function goToStep(step: number) {
  // Validation avant de changer d'étape
  if (step === 2 && !formDatas.user) {
    errorMessage.value = 'Veuillez d\'abord compléter les informations personnelles.'
    return
  }
  if (step === 3 && (!formDatas.user || !formDatas.photo)) {
    errorMessage.value = 'Veuillez d\'abord compléter les pièces d\'identité.'
    return
  }
  currentStep.value = step
  errorMessage.value = ''
  successMessage.value = ''
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function onAccountCreated(payload: PersonalInfosPayload) {
  formDatas.user = payload
  successMessage.value = 'Informations personnelles enregistrées avec succès.'
  goToStep(2)
}

function onIdentityDocumentsSaved(identityDocs: any) {
  formDatas.photo = identityDocs.photo
  formDatas.type_piece_identite = identityDocs.type_piece_identite
  formDatas.numero_piece_identite = identityDocs.numero_piece_identite
  formDatas.piece_recto = identityDocs.piece_recto
  formDatas.piece_verso = identityDocs.piece_verso
  successMessage.value = 'Pièces d\'identité validées avec succès.'
  goToStep(3)
}

function getCollectiviteName(collectiviteId: number | null): string {
  if (!collectiviteId) return ''
  const collectivite = formParams.collectivites.find(c => c.id === collectiviteId)
  return collectivite?.nom || ''
}

function onPayslipDocumentsSaved(payslipDocs: any) {
  formDatas.bulletin1 = payslipDocs.bulletin1
  formDatas.bulletin2 = payslipDocs.bulletin2
  formDatas.bulletin3 = payslipDocs.bulletin3
  // Ne pas soumettre automatiquement, l'utilisateur doit cliquer sur "Soumettre l'onboarding"
  // successMessage.value = 'Bulletins de salaire validés avec succès.'
}

function validateBeforeSubmit() {
  if (!formDatas.user) {
    errorMessage.value = 'Veuillez créer le compte avant de poursuivre.'
    return false
  }
  const requiredFiles = [
    { field: formDatas.photo, label: 'Photo de profil' },
    { field: formDatas.piece_recto, label: 'Pièce recto' },
    { field: formDatas.piece_verso, label: 'Pièce verso' },
    { field: formDatas.bulletin1, label: 'Bulletin 1' },
    { field: formDatas.bulletin2, label: 'Bulletin 2' },
    { field: formDatas.bulletin3, label: 'Bulletin 3' }
  ]
  const missing = requiredFiles.find(item => !item.field)
  if (missing) {
    errorMessage.value = `Le fichier "${missing.label}" est requis.`
    return false
  }
  return true
}

function resetFlow() {
  formDatas.user = null
  formDatas.photo = null
  formDatas.type_piece_identite = null
  formDatas.numero_piece_identite = null
  formDatas.piece_recto = null
  formDatas.piece_verso = null
  formDatas.bulletin1 = null
  formDatas.bulletin2 = null
  formDatas.bulletin3 = null
  currentStep.value = 1
  errorMessage.value = ''
  successMessage.value = ''
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function doSaveKYCDocuments() {
  if (!validateBeforeSubmit() || !formDatas.user) {
    if (!formDatas.user) {
      errorMessage.value = 'Veuillez compléter les informations personnelles.'
      await showAlert.warning('Veuillez compléter toutes les étapes avant de soumettre.')
    } else {
      await showAlert.warning('Veuillez compléter tous les champs obligatoires avant de soumettre.')
    }
    return
  }

  submitLoading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const formToUpload = new FormData()
    formToUpload.append('nom', formDatas.user.nom || '')
    formToUpload.append('prenom', formDatas.user.prenom || '')
    formToUpload.append('date_naissance', formDatas.user.date_naissance || '')
    formToUpload.append('age', String(formDatas.user.age || ''))
    formToUpload.append('date_engagement', formDatas.user.date_engagement || '')
    formToUpload.append('anciennete', String(formDatas.user.anciennete || ''))
    formToUpload.append('collectivite_id', String(formDatas.user.collectivite_id || ''))
    formToUpload.append('salaire', formDatas.user.salaire || '')
    formToUpload.append('quotite_cessible_pourcent', formDatas.user.quotite_cessible_pourcent || '')
    formToUpload.append('quotite_cessible_montant', formDatas.user.quotite_cessible_montant || '')
    formToUpload.append('telephone', formDatas.user.telephone || '')
    formToUpload.append('email', formDatas.user.email || '')

    formToUpload.append('photo', formDatas.photo!.file)
    formToUpload.append('type_piece_identite', formDatas.type_piece_identite || '')
    formToUpload.append('numero_piece_identite', formDatas.numero_piece_identite || '')
    formToUpload.append('piece_recto', formDatas.piece_recto!.file)
    formToUpload.append('piece_verso', formDatas.piece_verso!.file)
    formToUpload.append('bulletin1', formDatas.bulletin1!.file)
    formToUpload.append('bulletin2', formDatas.bulletin2!.file)
    formToUpload.append('bulletin3', formDatas.bulletin3!.file)

    const response = await onboardingService.createAccount(formToUpload)
    
    // Récupérer l'ID de l'utilisateur créé depuis la réponse
    // La réponse peut avoir différentes structures selon l'API
    const responseData = (response as any).data || response
    const userId = responseData?.user_id || responseData?.id || (response as any).user_id || (response as any).id
    
    // Si l'option d'envoi d'email est activée et qu'on a un ID utilisateur, envoyer l'email de confirmation
    if (sendKycEmail.value && userId) {
      try {
        await membersService.sendConfirmationEmail([userId])
        await showAlert.success(
          'Compte créé avec succès ! L\'email de confirmation a été envoyé.',
          'Inscription réussie'
        )
      } catch (emailError: any) {
        // Si l'envoi d'email échoue, on affiche quand même le succès de la création
        console.error('Erreur lors de l\'envoi de l\'email de confirmation:', emailError)
        await showAlert.success(
          'Compte créé avec succès ! L\'onboarding est terminé. (Note: L\'envoi de l\'email de confirmation a échoué)',
          'Inscription réussie'
        )
      }
    } else {
      // Afficher un popup de succès sans mention de l'email
      await showAlert.success(
        'Compte créé avec succès ! L\'onboarding est terminé.',
        'Inscription réussie'
      )
    }
    
    // Réinitialiser le formulaire et retourner à l'étape 1
    resetFlow()
  } catch (error: any) {
    const errorMsg = error.message || 'Une erreur est survenue lors de la création du compte.'
    errorMessage.value = errorMsg
    await showAlert.error(errorMsg, 'Erreur lors de la soumission')
  } finally {
    submitLoading.value = false
  }
}
</script>

<style scoped>
.tag-state {
  padding: 0.35rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--primary-color-rgb), 0.2);
  color: rgba(var(--color-black-rgb), 0.6);
  font-weight: 600;
}

.tag-state.active {
  background-color: rgba(var(--primary-color-rgb), 0.1);
  color: rgba(var(--primary-color-rgb), 1);
  border-color: rgba(var(--primary-color-rgb), 0.5);
}

.flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Wizard Stepper Styles */
.wizard-stepper {
  margin-bottom: 2rem;
}

.stepper-container {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem 0;
}

.stepper-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.stepper-step:not(.active):not(.completed) {
  cursor: default;
  opacity: 0.5;
}

.stepper-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(var(--color-grey-rgb, 200, 200, 200), 0.2);
  border: 2px solid rgba(var(--color-grey-rgb, 200, 200, 200), 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: rgba(var(--color-black-rgb), 0.6);
  transition: all 0.3s ease;
}

.stepper-step.active .stepper-circle {
  background-color: rgba(var(--primary-color-rgb), 0.1);
  border-color: rgba(var(--primary-color-rgb), 1);
  color: rgba(var(--primary-color-rgb), 1);
}

.stepper-step.completed .stepper-circle {
  background-color: rgba(var(--primary-color-rgb), 1);
  border-color: rgba(var(--primary-color-rgb), 1);
  color: white;
}

.stepper-label {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(var(--color-black-rgb), 0.6);
  transition: all 0.3s ease;
}

.stepper-step.active .stepper-label {
  color: rgba(var(--primary-color-rgb), 1);
  font-weight: 600;
}

.stepper-step.completed .stepper-label {
  color: rgba(var(--primary-color-rgb), 0.8);
}

.stepper-line {
  flex: 1;
  height: 2px;
  background-color: rgba(var(--color-grey-rgb, 200, 200, 200), 0.3);
  margin: 0 1rem;
  margin-top: -20px;
  transition: all 0.3s ease;
}

.stepper-line.completed {
  background-color: rgba(var(--primary-color-rgb), 1);
}

@media (max-width: 768px) {
  .stepper-container {
    padding: 0.5rem;
  }
  
  .stepper-circle {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
  }
  
  .stepper-label {
    font-size: 0.75rem;
  }
  
  .stepper-line {
    margin: 0 0.5rem;
    margin-top: -16px;
  }
}

</style>
